import axios, { AxiosInstance } from 'axios';
import { getStoredToken } from '@/utils/auth';
import type {
  ListGamesResponse,
  GetGameResponse,
  RevealPackResponse,
  PickCardResponse,
  GetUserEntryResponse,
  GetDayFixturesResponse,
  GetContestRulesResponse,
} from '@/types/reveal';

import { resolveTenantFromUrl } from '@low6-pickem/frontend-shared';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Tenant slug resolved from URL path or environment variable
const TENANT_SLUG = resolveTenantFromUrl();

const client: AxiosInstance = axios.create({
  baseURL: `${API_BASE}/api`,
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
    'X-Tenant-Slug': TENANT_SLUG,
  },
});

function getAuthHeaders(): Record<string, string> {
  const token = getStoredToken();
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/** Attempts for a retried POST — the initial send plus two retries. */
const RETRY_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 400;

/**
 * Whether a failed request is worth sending again.
 *
 * Retry only what a repeat could plausibly fix: no response at all (dropped
 * connection, timeout) or a 5xx. A 4xx is the server's considered answer — for the
 * pick endpoint that includes 409 "pick window has closed" and 404 "card not found
 * in your entry" — and repeating it just delays showing the player the reason.
 */
function isRetryableError(err: unknown): boolean {
  if (!axios.isAxiosError(err)) return false;
  if (!err.response) return true; // network error / timeout — no response reached us
  return err.response.status >= 500;
}

/**
 * POST with bounded retries and linear backoff.
 *
 * Used for requests where losing the write costs the player something they cannot
 * recover (DKPLAT-724). Only ever applied to endpoints that are safe to repeat.
 */
async function postWithRetry<T>(url: string, body: unknown): Promise<T> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= RETRY_ATTEMPTS; attempt++) {
    try {
      const { data } = await client.post<T>(url, body, { headers: getAuthHeaders() });
      return data;
    } catch (err) {
      lastError = err;
      // Give up immediately on a considered answer, or once attempts are spent —
      // the caller surfaces the error and the UI stays on the picker.
      if (!isRetryableError(err) || attempt === RETRY_ATTEMPTS) break;
      await new Promise(resolve => setTimeout(resolve, RETRY_BASE_DELAY_MS * attempt));
    }
  }

  throw lastError;
}

export const dailyRevealApi = {
  /** Public — list all daily reveal games */
  async listGames(): Promise<ListGamesResponse> {
    const { data } = await client.get<ListGamesResponse>('/daily-reveals', {
      headers: getAuthHeaders(),
    });
    return data;
  },

  /** Authenticated — get game overview with day + user entry status */
  async getGame(gameId: string): Promise<GetGameResponse> {
    const { data } = await client.get<GetGameResponse>(`/daily-reveals/${gameId}`, {
      headers: getAuthHeaders(),
    });
    return data;
  },

  /** Authenticated — reveal a pack for the given day (idempotent) */
  async revealPack(dayId: string): Promise<RevealPackResponse> {
    const { data } = await client.post<RevealPackResponse>(
      `/daily-reveals/days/${dayId}/reveal`,
      {},
      { headers: getAuthHeaders() }
    );
    return data;
  },

  /**
   * Authenticated — pick a card from the revealed pack.
   *
   * Retried on transient failures only (DKPLAT-724). Losing this request costs the
   * player their entry for the day and, until the same ticket, did so silently — so a
   * dropped connection or a brief 5xx is worth a second attempt rather than a support
   * ticket the next morning.
   *
   * Deliberately NOT retried on 4xx: 409 ("game already started", "pick window closed")
   * and 404 are considered answers, not failures. Repeating them only delays telling
   * the player no.
   *
   * Safe to repeat — the endpoint sets the same card for the same entry, so a retry
   * after a lost response lands on the same state as the request it replaces.
   */
  async pickCard(dayId: string, cardId: string): Promise<PickCardResponse> {
    return postWithRetry<PickCardResponse>(`/daily-reveals/days/${dayId}/pick`, { cardId });
  },

  /** Authenticated — get user's existing entry for a day */
  async getUserEntry(dayId: string): Promise<GetUserEntryResponse> {
    const { data } = await client.get<GetUserEntryResponse>(`/daily-reveals/days/${dayId}/entry`, {
      headers: getAuthHeaders(),
    });
    return data;
  },

  /** Public — get live fixture data for a day */
  async getDayFixtures(dayId: string): Promise<GetDayFixturesResponse> {
    const { data } = await client.get<GetDayFixturesResponse>(
      `/daily-reveals/days/${dayId}/fixtures`
    );
    return data;
  },

  /** Public — get contest rules for the active daily reveal contest */
  async getContestRules(gameId: string): Promise<GetContestRulesResponse> {
    const { data } = await client.get<GetContestRulesResponse>(`/pickems/${gameId}/rules`);
    return data;
  },
};
